import { supabase } from "@/integrations/supabase/client";

export type GlossaryEntry = {
  term: string;              // canonical display term (e.g. "UNA (unincorporated association)")
  aliases: string[];         // matchable variants (case-insensitive), e.g. ["UNA", "unincorporated association"]
  slug: string;              // anchor id (e.g. "una")
  definition: string;        // definition text (plain-ish)
};

/**
 * Slugify a term per the glossary spec:
 * - if there's a parenthetical containing a space or a 3+ char alphabetic run,
 *   use the text before it; otherwise keep the whole term (so "Section 508(c)(1)(A)" survives).
 * - lowercase; whitespace -> "-"; keep [a-z0-9-]; strip everything else.
 */
export function slugifyTerm(raw: string): string {
  let src = raw.trim().replace(/\.+$/, "");
  const parenIdx = src.indexOf("(");
  if (parenIdx > 0) {
    const close = src.indexOf(")", parenIdx);
    const inner = close > 0 ? src.slice(parenIdx + 1, close) : "";
    if (/\s/.test(inner) || /[A-Za-z]{3,}/.test(inner)) {
      src = src.slice(0, parenIdx).trim();
    }
  }
  return src
    .toLowerCase()
    .replace(/\s+/g, "-")
    .replace(/[^a-z0-9-]/g, "");
}

/**
 * Parse aliases from a raw term.
 * "UNA (unincorporated association)" -> ["UNA", "unincorporated association"]
 * "Record (the)" -> ["Record", "the Record"]  (never bare "the")
 * "Engine" -> ["Engine"]
 * A bare leading article ("the", "a", "an") is never allowed as an alias — it
 * would match every occurrence of the word in ordinary prose.
 */
function isBareArticle(s: string): boolean {
  return /^(the|a|an)$/i.test(s.trim());
}

function aliasesFor(raw: string): string[] {
  const base = raw.trim().replace(/\.+$/, "");
  const out: string[] = [];
  const m = base.match(/^(.+?)\s*\(([^)]+)\)\s*$/);
  if (m) {
    const short = m[1].trim();
    const inner = m[2].trim();
    if (short) out.push(short);
    if (isBareArticle(inner)) {
      // "Record (the)" -> also expose the distinctive multi-word form.
      if (short) out.push(`${inner} ${short}`);
    } else if (/\s/.test(inner) || /[A-Za-z]{3,}/.test(inner)) {
      out.push(inner);
    }
    if (!out.length) out.push(base);
  } else {
    out.push(base);
  }
  // Never allow a bare article to be an alias.
  return out.filter((a) => !isBareArticle(a));
}

/**
 * Parse the FAQ-000 markdown body into a list of entries.
 * Entries are paragraphs starting with a bolded term followed by a period, e.g.
 *   **Engine.** definition text ...
 *   **UNA (unincorporated association).** definition text ...
 * Accepts either `**Term.**` (period inside bold) or `**Term**.` (period outside).
 */
export function parseDictionary(markdown: string): GlossaryEntry[] {
  const paragraphs = markdown.split(/\n\s*\n/);
  const entries: GlossaryEntry[] = [];
  const seenSlugs = new Set<string>();
  const re = /^\*\*([^*\n]+?)\*\*\.?\s*([\s\S]*)$/;
  for (const p of paragraphs) {
    const trimmed = p.trim();
    if (!trimmed.startsWith("**")) continue;
    const m = trimmed.match(re);
    if (!m) continue;
    const rawTerm = m[1].trim();
    // Skip headings-inside-bold or things that don't look like a term (must not span multiple lines).
    if (rawTerm.length > 80 || /\n/.test(rawTerm)) continue;
    const slug = slugifyTerm(rawTerm);
    if (!slug || seenSlugs.has(slug)) continue;
    seenSlugs.add(slug);
    const definition = m[2].trim();
    entries.push({
      term: rawTerm.replace(/\.+$/, ""),
      aliases: aliasesFor(rawTerm),
      slug,
      definition,
    });
  }
  return entries;
}

// ---------- Cache ----------

let cache: GlossaryEntry[] | null = null;
let pending: Promise<GlossaryEntry[]> | null = null;

export function loadGlossary(): Promise<GlossaryEntry[]> {
  if (cache) return Promise.resolve(cache);
  if (!pending) {
    pending = (async () => {
      const { data } = await supabase
        .from("sop_documents" as any)
        .select("body_markdown")
        .eq("doc_id", "FAQ-000")
        .eq("status", "published")
        .maybeSingle();
      const body = (data as any)?.body_markdown || "";
      const entries = parseDictionary(body);
      cache = entries;
      return entries;
    })().catch(() => {
      pending = null;
      return [];
    });
  }
  return pending;
}

export function invalidateGlossary() {
  cache = null;
  pending = null;
}
